import { Component, ChangeDetectorRef, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { SubscriptionService } from "../../../shared/services/subscription.service";
import { AuthService } from "../../../shared/services/auth.service";
import { PlanCard } from "src/app/shared/components/plan-card/plan-card.model";
import { properties } from "src/environments/properties";
import {
  _windowUtil,
  convertCurrencyToWords,
} from "src/app/shared/utils/ui-utils";

@Component({
  selector: "app-change-plan-summary-v2",
  templateUrl: "./change-plan-summary-v2.component.html",
  styleUrl: "./change-plan-summary-v2.component.scss",
  providers: [SubscriptionService],
})
export class ChangePlanSummaryV2Component implements OnInit {
  userInfo: any;
  updatePlan: boolean;

  annualPlan: any;
  monthlyPlan: any;

  changeCode: any;
  fetchedPlans: any;
  userSubscriptionDetails: any;
  tenderInfo: any;
  userEnrollmentDetails: any;
  termsID: any;
  authUserInfo: any;
  showPlanTypes = true;

  showLoader = true;

  currentEnvironment;
  properties = properties;

  isRedirectedFromPrechurn = false;
  referrerPath: string = "";

  retentionPlanDiscount: any;
  isB2B: boolean;
  isRedirectedFromSubscribePage: boolean = false;

  public windowUtil = _windowUtil(window);
  convertCurrencyToWords = convertCurrencyToWords;

  combinedPlans = [];
  cardType: string = 'changePlan';

  enableSnap = this.properties.featureFlags.enableSnap;
  enableForUPlus: boolean = this.properties.featureFlags.enableForUPlus;
  programDisplayData = this.enableForUPlus
    ? this.properties.programType.forUPlus
    : this.properties.programType.freshpass;
  displaySubTypeLink = false;
  isServiceError: boolean;
  errorMessage: string;

  constructor(
    public subscriptionService: SubscriptionService,
    public authService: AuthService,
    public activeRoute: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private cdr: ChangeDetectorRef
  ) {
    this.isB2B = this.authService.checkB2B();
    this.updatePlan = false;
    this.processRedirectionRoutingLogic();
  }

  ngOnInit(): void {
    this.setTitle(`${this.programDisplayData.title} - Change Plan`);
    this.currentEnvironment = this.subscriptionService.getEnvironment();
    if (this.activeRoute.data) {
      this.activeRoute.data.subscribe({
        next: (data) => {
          let isSessionValid = this.authService.isTokenValidForSession(
            data.tokenObject
          );
          if (isSessionValid) {
            const sessionData = {
              ...data.tokenObject,
              authToken: data.tokenObject["SWY_SHOP_TOKEN"],
            };
            if (sessionData) {
              this.userInfo = sessionData;

              //Use case: User clicks on (partners) CTA, is enrolled, and gets redirected to changeplan screen from subscribed page
              if (
                this.isRedirectedFromSubscribePage &&
                !this.userSubscriptionDetails
              ) {
                this.getSubscriptionDetails();
              } else {
                //Normal use case: User clicks on change plan from manage page
                this.fetchDeliverySubscriptionTenders();
              }
            }
          } else {
            this.authService.redirectToSSO(data.tokenObject);
          }
        },
        error: (err) => {
          this.authService.redirectToSSO({});
        },
      });
    }
  }

  processRedirectionRoutingLogic() {
    //read path from url to determine if user is redirected from /freshpass/manage/subscribe and document referrer path is from expedia or partners
    if (this.checkIfRedirectedFromSubscribePageWithExpedia()) {
      this.isRedirectedFromSubscribePage = true;
    } else {
      const routerParams = this.router?.getCurrentNavigation().extras.state;
      if (
        routerParams?.isRedirectedFromManageLink &&
        routerParams?.userSubscriptionDetails
      ) {
        this.userSubscriptionDetails = routerParams.userSubscriptionDetails;
      } else if (
        routerParams?.isRedirectedFromPrechurnDiscount &&
        routerParams?.userSubscriptionDetails &&
        routerParams?.retentionPlanDiscount
      ) {
        this.isRedirectedFromPrechurn = true;
        this.updatePlan = true;
        this.showPlanTypes = false;
        this.userSubscriptionDetails = routerParams?.userSubscriptionDetails;
        this.retentionPlanDiscount = routerParams.retentionPlanDiscount;
      } else {
        this.router.navigate([
          this.programDisplayData?.urlPath?.MANAGE_PLAN_PATH,
        ]);
      }
    }
  }

  fetchPlans() {
    this.showLoader = true;
    this.subscriptionService
      .getUserChangePlanDetails(this.userInfo, this.currentEnvironment)
      .subscribe({
        next: (data) => {
          this.showLoader = false;
          if (data) {
            //Originial structure - this.fetchedPlans = data;
            this.fetchedPlans = {
              Data: {
                subscriptions: [
                  {
                    ...data.currentPlan,
                    selected: true,
                  },
                  data.availablePlans[0],
                ],
              },
            };

            if (data.currentPlan?.caption?.toLowerCase().includes("monthly")) {
              this.monthlyPlan = data.currentPlan;
              this.annualPlan = data.availablePlans[0];
            } else {
              this.annualPlan = data.currentPlan;
              this.monthlyPlan = data.availablePlans[0];
            }

            this.preparePlanCards(data);
          }
        },
        error: (err) => {
          this.showLoader = false;
          this.setErrorMessage(err?.errorCode);
        },
      });
  }

  fetchDeliverySubscriptionTenders() {
    this.subscriptionService
      .getTenders(this.userInfo, this.currentEnvironment)
      .subscribe({
        next: (tenderData) => {
          if (
            tenderData &&
            tenderData.paymentMethods &&
            tenderData.paymentMethods.length > 0
          ) {
            tenderData.paymentMethods.forEach((tenderObj) => {
              if (
                tenderObj.primaryPurposes.includes(
                  this.programDisplayData.manage.DELIVERY_SUBSCRIPTION
                )
              ) {
                this.tenderInfo = tenderObj;
                //Prechurn flow
                if (
                  this.isRedirectedFromPrechurn &&
                  this.retentionPlanDiscount
                ) {
                  this.changePlan(this.retentionPlanDiscount.code);
                } else {
                  //Standard change plan flow
                  this.fetchPlans();
                }
              }
            });
          }
        },
        error: (err) => {
          this.errorMessage = this.setErrorMessage(err?.errorCode);
        },
      });
  }

  goToUpdate() {
    this.updatePlan = true;
  }

  changePlan(code: any, plan?: any) {
    if (!code) {
      this.windowUtil.setWindowLocationHref(
        this.programDisplayData?.urlPath?.GOV_ASSIST_PATH
      );
    } else {
      this.showLoader = true;
      this.changeCode = code;
      this.subscriptionService
        .getTerms(this.userInfo, this.currentEnvironment)
        .subscribe({
          next: (data) => {
            this.showLoader = false;
            if (data) {
              this.termsID = data.id;
              //get GID from userInfo call instead of getEnrollmentDetails
              this.userEnrollmentDetails = {
                gid: this.userInfo.customerId,
              };
              this.showPlanTypes = false;
            }
          },
          error: (err) => {
            this.showLoader = false;
            this.errorMessage = this.setErrorMessage(err?.errorCode);
          },
        });
      this.updatePlan = true;
    }
  }

  setTitle(newTitle: string): void {
    this.titleService.setTitle(newTitle);
  }

  preparePlanCards(data) {
    // Originial structure - const subscriptions = data.Data.subscriptions || [];
    const subscriptions = this.fetchedPlans.Data.subscriptions;

    const promotionPlans =
      data.promotionPlans && !this.isB2B
        ? data.promotionPlans.filter(
            (plan) => plan.planName === this.programDisplayData?.planCard?.SNAP
          )
        : [];

    const combinedPlansArray = [
      ...subscriptions.map((plan) =>
        this.mapCardPlans(plan, plan.caption, plan.isSheerVerified)
      ),
      ...promotionPlans.map((plan) =>
        this.mapCardPlans(plan, plan.planName, false)
      ),
    ];

    combinedPlansArray.sort(
      (a: any, b: any) => b.details.selected - a.details.selected
    );
    this.combinedPlans = combinedPlansArray;

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  public mapCardPlans(
    plan: any,
    key: string,
    isSheerVerified: boolean
  ): PlanCard {
    const annualPlanCard: PlanCard = {
      title: key,
      action: this.programDisplayData?.planCard?.selectAction,
      banner: plan.pricingDetailLine1,
      bannerAnouncement: convertCurrencyToWords(plan.pricingDetailLine1),
      subBanner: this.programDisplayData?.planCard?.annualPlanDescription,
      subBannerAnouncement: "",
      details: plan,
    };

    const monthlyPlanCard: PlanCard = {
      title: key,
      action: this.programDisplayData?.planCard?.selectAction,
      banner: plan.pricingDetailLine1,
      bannerAnouncement: convertCurrencyToWords(plan.pricingDetailLine1),
      subBanner: this.programDisplayData?.planCard?.monthlyPlanDescription,
      subBannerAnouncement: "",
      details: plan,
    };

    const snapPlanCard: PlanCard = {
      title: "",
      action: "",
      banner: plan.discountText,
      subBanner: this.programDisplayData?.planCard?.SNAPPlanDescription,
      details: plan,
    };

    if (key === this.programDisplayData?.planCard?.AnnualPlan) {
      return this.configureAnnualPlan(annualPlanCard, plan, isSheerVerified);
    } else if (key === this.programDisplayData?.planCard?.MonthlyPlan) {
      return this.configureMonthlyPlan(monthlyPlanCard, plan, isSheerVerified);
    } else if (key === this.programDisplayData?.planCard?.SNAP) {
      return this.configureSnapPlan(snapPlanCard, plan);
    }

    return null;
  }

  public configureAnnualPlan(
    planCard: PlanCard,
    plan: any,
    isSheerVerified: boolean
  ): PlanCard {
    if (isSheerVerified) {
      planCard.banner = `$${plan.planPrice} <s class="strick">$${plan.regularPlanPrice}</s> ${plan.planPriceDetail}`;
      planCard.bannerAnouncement = convertCurrencyToWords(plan.planPrice);
      planCard.subBanner =
        this.programDisplayData?.planCard?.planDescriptionForSNAPVerified.replace(
          "{pricingDetailLine1}",
          plan.pricingDetailLine1.replace(/[\(\)']+/g, "")
        );
      planCard.subBannerAnouncement = convertCurrencyToWords(
        planCard.subBanner
      );

      //scenario where the user has become snap verified, but plan has not fully converted to snap pricing.
      if (this.userSubscriptionDetails?.promoInfo && plan?.selected && plan?.pendingPlanDetails) {
          planCard.banner = plan.pricingDetailLine1;
          planCard.bannerAnouncement = convertCurrencyToWords(planCard.banner);
          planCard.subBanner =
            this.programDisplayData?.planCard?.annualPlanDescription
              .replace(
                "{monthlyPrice}",
                plan.pendingPlanDetails?.pricingDetailLine2.replace(/[\(\)']+/g, "")
              )
              .replace(
                "{price}",
                plan?.pendingPlanDetails?.pricingDetailLine1
              );
          planCard.subBannerAnouncement = convertCurrencyToWords(
            planCard.subBanner
          );
          planCard.benefits = [
            {
              icon: this.programDisplayData?.planCard?.checkmarkCircle,
              label: this.userSubscriptionDetails.promoInfo,
              textColor: this.programDisplayData?.planCard?.successColor,
            }
          ];
          if (!this.enableForUPlus) {
            planCard.benefits.push({
              icon: this.programDisplayData?.planCard?.creditIcon,
              label: this.programDisplayData?.planCard?.annualCreditLabel
            });
          }
      }
    } else {
      if (plan.isDiscountedPrice && plan.discountDuration) {
        planCard.subBanner =
          this.programDisplayData?.planCard?.annualDiscountPlanDescription?.replace(
            "{renewPrice}",
            " at $" + plan.regularPlanPrice.replace(/[\(\)']+/g, "") + "/year"
          );
        planCard.subBannerAnouncement = convertCurrencyToWords(
          planCard.subBanner
        );
      } else {
        if (
          parseFloat(this.userSubscriptionDetails.pendingFee).toString() ===
          plan.planPrice
        ) {
          planCard.banner = `$${plan.regularPlanPrice}${plan.planPriceDetail}`;
          planCard.bannerAnouncement = convertCurrencyToWords(planCard.banner);
          planCard.subBanner =
            this.programDisplayData?.planCard?.annualPlanDescription
              .replace(
                "{price}",
                this.userSubscriptionDetails.pricingDetailLine2.replace(
                  /[\(\)']+/g,
                  ""
                )
              )
              .replace("<br/>", ". ")
              .replace(
                "{renewPrice}",
                ` at $${plan.pricingDetailLine1.replace(/[\(\)']+/g, "")}`
              );
          planCard.subBannerAnouncement = convertCurrencyToWords(
            planCard.subBanner
          );
        } else {
          //regular scenario: current user has pending plan: show current pricing and renewal pricing to pending plan
          if (plan?.pendingPlanDetails) {
            planCard.subBanner = planCard.subBanner
              .replace("{price}", plan?.pendingPlanDetails?.pricingDetailLine1)
              .replace(
                "{monthlyPrice}",
                plan?.pricingDetailLine2.replace(/[\(\)']+/g, "")
              );
            planCard.subBannerAnouncement = convertCurrencyToWords(
              planCard.subBanner
            );
          } else {
            planCard.subBanner = planCard.subBanner
              .replace("{price}", plan.pricingDetailLine1)
              .replace(
                "{monthlyPrice}",
                plan.pricingDetailLine2.replace(/[\(\)']+/g, "")
              );
            planCard.subBannerAnouncement = convertCurrencyToWords(
              planCard.subBanner
            );
          }
        }
      }
    }
    return planCard;
  }

  public configureMonthlyPlan(
    planCard: PlanCard,
    plan: any,
    isSheerVerified: boolean
  ): PlanCard {
    if (isSheerVerified) {
      planCard.banner = `$${plan.planPrice} ${
        plan.isSheerVerified
          ? `<s class="strick">$${plan.regularPlanPrice}</s>`
          : ""
      } ${plan.planPriceDetail}`;
      planCard.bannerAnouncement = convertCurrencyToWords(planCard.banner);
      planCard.subBanner =
        this.programDisplayData?.planCard?.monthlyDescriptionForSNAPVerifiedRef?.replace(
          "{price}",
          plan.pricingDetailLine1.replace(/[\(\)']+/g, "")
        );
      planCard.subBannerAnouncement = convertCurrencyToWords(
        planCard.subBanner
      );

      if (this.userSubscriptionDetails.promoInfo && plan.selected) {
        planCard.banner = `$${plan.regularPlanPrice}${plan.planPriceDetail}`;
        planCard.subBanner =
        this.programDisplayData?.planCard?.monthlyDescriptionForSNAPVerifiedRef?.replace(
          "{price}",
          plan?.pendingPlanDetails?.pricingDetailLine1.replace(/[\(\)']+/g, "")
        );
        planCard.bannerAnouncement = convertCurrencyToWords(planCard.banner);
        planCard.benefits = [
          {
            icon: this.programDisplayData?.planCard?.checkmarkCircle,
            label: this.userSubscriptionDetails.promoInfo,
            textColor: this.programDisplayData?.planCard?.successColor,
          },
        ];
      }
    } else {
      if (plan?.isDiscountedPrice && plan?.discountDuration) {
        let monthlyPrice = ` at $${plan?.regularPlanPrice}/mo after ${plan?.discountDuration} months`;
        planCard.subBanner = planCard.subBanner
          ? planCard.subBanner.replace("{price}", monthlyPrice)
          : "";
        planCard.subBannerAnouncement = convertCurrencyToWords(
          planCard.subBanner
        );
      } else {
        planCard.subBanner = planCard.subBanner
          ? planCard.subBanner.replace("{price}", plan.pricingDetailLine1)
          : "";
        planCard.subBannerAnouncement = convertCurrencyToWords(
          planCard.subBanner
        );
      }
    }
    return planCard;
  }

  public configureSnapPlan(snapPlanCard: PlanCard, plan: any): PlanCard {
    if (plan?.planDetails?.title?.includes("Gov assisted")) {
      snapPlanCard.title = plan.planDetails.title + " Plans";
    } else {
      snapPlanCard.title = plan.planDetails.title;
    }
    snapPlanCard.action = plan.planDetails.verificationDisplayText;
    snapPlanCard.banner = plan.discountText
      ? plan.discountText
      : this.programDisplayData?.changePlanSummary?.offYourSubscription;
    return snapPlanCard;
  }

  getSubscriptionDetails() {
    this.subscriptionService
      .getUserSubscriptionDetails(this.userInfo, this.currentEnvironment)
      .subscribe({
        next: (data) => {
          if (data?.Data) {
            this.userSubscriptionDetails = data.Data;
            this.redirectToManageIfChangePlanNotAllowed();
          }
        },
        error: (error) => {
          this.setErrorMessage(error?.errorCode);
        },
      });
  }

  checkIfRedirectedFromSubscribePageWithExpedia = (
    queryString = window.location.search
  ) => {
    const urlParams = new URLSearchParams(queryString);
    return (
      urlParams.get("webSource") ===
        this.programDisplayData?.WEBSOURCE?.EXPEDIA &&
      !!urlParams.get("referrer")
    );
  };

  redirectToManageIfChangePlanNotAllowed() {
    if (!this.userSubscriptionDetails?.isPlanChangeAvailable) {
      this.router.navigate([
        this.programDisplayData?.urlPath?.MANAGE_PLAN_PATH,
      ]);
    } else {
      this.fetchDeliverySubscriptionTenders();
    }
  }

  setErrorMessage(errorCode: string) {
    this.isServiceError = true;
    switch (errorCode) {
      default:
        this.errorMessage =
          this.programDisplayData?.errorMessages.DEFAULT_ERROR_MESSAGE;
    }
    return this.errorMessage;
  }
}
